import React, { useEffect, useRef, useState } from "react";
import { selectUserById, useGetUserByIdQuery } from "../features/users/usersApiSlice";
import { selectProductById } from "../features/products/productsApiSlice";
import useAuth from '../hooks/useAuth';
import { useSelector } from "react-redux";
import { formatRelativeTime } from "../utility/relativeTime";
import AddReview from "./AddReview";
import ClipLoader from "react-spinners/ClipLoader";

import { useDeleteReviewMutation, useUpdateReviewMutation } from "../features/review/reviewsApiSlice";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { userImageUrlPrefix } from "../assets/data";

const ProductReview = ({ need, productId, productReview, refetch }) => {
  const { id } = useAuth();
  const {data: user} = useGetUserByIdQuery(productReview.user);
  // const product = useSelector((state) => selectProductById(state, productReview.product));
  const [updateReview, { isLoading: updateLoading, isSuccess: updateSuccess, isError: updateIsError, error: updateError }] =
    useUpdateReviewMutation();
  const [reply, setReply] = useState(false);
  const [replyBox, setReplyBox] = useState(false);
  const [priceValue, setPriceValue] = useState(productReview.offer.value);
  const [currency, setCurrency] = useState(productReview.offer.currency);
  const [rContent, setRContent] = useState(productReview.content);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const textareaRef = useRef(null);

  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleTextareaChange = (event) => {
    setRContent(event.target.value);
  };

  const [displayedReplies, setDisplayedReplies] = useState(3);
  const imageUrl = `${userImageUrlPrefix}${user?.userImage}`;

  const handleReplyButtonClick = () => {
    setReply(!reply);
  };

  const handleReviewUpdateClick = async () => {
    if(need === "Sell"){
      await updateReview({ id: productReview._id, content: rContent });
    }else  {
      await updateReview({ 
        id: productReview._id,
        content: rContent, 
        offer: { 
          currency: currency, 
          value: priceValue 
        }
      });
    }
    setIsDialogOpen(false);
  };

  const [deleteReview, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useDeleteReviewMutation();

  useEffect(() => {
    if (isSuccess) {
        refetch();
    } else if (isError) {
      console.log(error?.data?.message);
    } else if (updateSuccess) {
      refetch();
    }else if(updateIsError){
      console.log(updateError?.data?.message);
    }
  }, [isSuccess, isError, error, updateSuccess, updateIsError, updateError, refetch]);

  const onDeleteReviewClicked = async () => {
    await deleteReview({ id: productReview._id });
  }

  const handleShowMoreReplies = () => {
    setDisplayedReplies((prev) => prev + 5);
  };

  const handleShowLessReplies = () => {
    setDisplayedReplies(3);
  };

  const handleCurrencyChange = (value) => {
    setCurrency(value);
  };

  const formatNumber = (number) => {
    const parts = number.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };
  const onPriceValueChanged = (e) => {
    let rawValue = e.target.value.replace(/,/g, '');
    if (/^[0-9.]*$/.test(rawValue)) {
      const formattedValue = formatNumber(rawValue);
      setPriceValue(formattedValue);
    }
  };

  useEffect(() => {
    if (!isDialogOpen) {
      setRContent(productReview.content);
    }
  }, [isDialogOpen, productReview.content]);

  let content;
  if(isLoading) {
    content = (
      <div className="w-full h-full flex justify-start items-center">
        <ClipLoader
          color= {"black"}
          size={30}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
        <p className="text-[1.2rem] text-black">Deleting...</p>
      </div>
    );
  }else if(isSuccess) {
    content = (<></>);
  }else {
    content = (
      <>
        <div className={`border-b p-2 pb-1 border-neutral-400 w-full flex justify-start items-start relative`}>
          {user?.userImage && (
            <img
              src={imageUrl}
              alt={`${user.firstName}'s profile`}
              className="p-1 w-12 h-12 min-w-12 rounded-full mr-2 object-cover"
            />
          )}
          <div className="group relative flex-grow">
            {productReview?.respondTo && (
              <>
                <p className="leading-none text-neutral-500 text-[.8rem] inline">
                  @{user?.firstName} {productReview?.respondTo?.name === "none" ? productReview?.respondTo?.reply === null? "" : "to " : `to @${productReview?.respondTo?.name}'s `}
                </p>
                <span className="leading-none text-black text-[.8rem]">{productReview?.respondTo?.reply === null ? "" : `(${productReview?.respondTo?.reply?.content})`}</span>
              </>
            )}
            {need === "Request" && (
              <p className="text-[1.1rem] whitespace-pre-wrap max-w-[100%] leading-none overflow-hidden">
                Offer - <span className="text-green-500 font-bold">{productReview.offer.currency}</span> <span className="font-bold">{productReview.offer.value}</span>
              </p>
            )}
            <p
              className={`text-[.9rem] whitespace-pre-wrap max-w-[100%] mb-1 overflow-hidden ${
                expanded ? "" : "line-clamp-2"
              }`}
              onClick={toggleExpanded}
            >
              {productReview.content}
            </p>
            <p className="leading-none text-[.7rem] py-1 text-neutral-500">{formatRelativeTime(productReview.createdAt)}</p>

            <div className="flex justify-end items-end mt-3">
              {id === productReview.user ? (
                <>
                  <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                      <button className="text-[.8rem] text-neutral-500 p-[2px] px-2 flex justify-center items-center border-l border-r border-t border-solid border-neutral-400 rounded-tl-lg hover:bg-blue-200 hover:text-blue-700 hover:border-blue-700 leading-none">
                        <span className="material-icons text-[.8rem] mr-1 leading-none">edit</span> Edit
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[450px] bg-custom-content-bg">
                      <DialogHeader>
                        <DialogTitle>Edit Review</DialogTitle>
                      </DialogHeader>
                      {need === "Request" && (
                        <div className="w-full flex space-x-4">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="etb"
                              name="currency"
                              value="ETB"
                              checked={currency === "ETB"}
                              onChange={() => handleCurrencyChange("ETB")}
                              className="cursor-pointer"
                            />
                            <label htmlFor="etb" className="ml-2 cursor-pointer">
                              ETB
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="usd"
                              name="currency"
                              value="$"
                              checked={currency === "$"}
                              onChange={() => handleCurrencyChange("$")}
                              className="cursor-pointer"
                            />
                            <label htmlFor="usd" className="ml-2 cursor-pointer">
                              $
                            </label>
                          </div>
                          <input
                            type="text"
                            onChange={onPriceValueChanged}
                            value={priceValue}
                            className={`w-full px-2 py-1 bg-white shadow-md rounded-md leading-[22px] outline-none bg-transparent`}
                            placeholder="0.00"
                          />
                        </div>
                      )}
                      <textarea
                        ref={textareaRef}
                        className={`w-full leading-5 text-[.9rem] p-2 border-none resize-none overflow-y-auto outline-none rounded-lg shadow-md focus:h-auto`}
                        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                        rows={15}
                        placeholder={`Edit here...`}
                        value={rContent}
                        onChange={handleTextareaChange}
                        tabIndex= {-1}
                      ></textarea>
                      <DialogFooter>
                        <Button type="submit" onClick={() => {handleReviewUpdateClick()}}>
                          {updateLoading ? (
                            <ClipLoader
                              color= {"white"}
                              size={30}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                            />
                          ) : (
                            "Save changes"
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-[.8rem] text-neutral-500 p-[2px] px-2 flex justify-center items-center border-r border-t border-solid border-neutral-400 rounded-tr-lg hover:bg-red-200 hover:text-red-500 hover:border-red-500 leading-none">
                        <span className="material-icons text-[.8rem] mr-1 leading-none">delete</span> Delete
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-custom-content-bg">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete your {need === "Sell" ? "review and all its reply's" : "Offer"} from our servers.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDeleteReviewClicked}>
                          {isLoading ? (
                            <ClipLoader
                              color= {"white"}
                              size={30}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                            />
                          ) : (
                            "Continue"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <button 
                  className="text-[.8rem] text-neutral-500 p-[2px] px-2 flex justify-center items-center border border-solid border-neutral-400 rounded-lg hover:bg-blue-200 hover:text-blue-700 hover:border-blue-700 leading-none"
                  onClick={() => setReply(!reply)}
                >
                  <span className="material-icons text-[.8rem] mr-1 leading-none">reply</span> Reply
                </button>
              )}
            </div>
          </div>
        </div>
        {reply && (
          console.log(need),
          
          <AddReview productId={productId} need={need} useFor={"reply"} reviewId={productReview._id} handleReplyButtonClick={handleReplyButtonClick} refetch={refetch}>
            <button onClick={() => setReply(!reply)}>Cancel</button>
          </AddReview>
        )}
        {productReview.replies && productReview.replies.length > 0 && (
          <div className="w-full pl-20">
            <button className="hover:bg-custom-main-bg text-amber-500 hover:text-black py-1 pl-1 pr-3 text-[.8rem] leading-none rounded-2xl flex justify-center items-center" onClick={() => {setReplyBox(!replyBox); handleShowLessReplies();}}>
              <span className="material-icons text-[1.3rem]">{replyBox ? "arrow_drop_up" : "arrow_drop_down"}</span>
              {productReview.replies.length} replies
            </button>
          </div>
        )}
        {productReview.replies && productReview.replies.length > 0 && replyBox && (
          <div className="w-[90%] flex flex-col justify-start items-start gap-2">
            {productReview.replies.slice(0, displayedReplies).map((reply) => (
              <ProductReview key={reply._id} productId={productId} need={need} productReview={reply} refetch={refetch} />
            ))}
            <div className="flex justify-start items-center flex-wrap">
              {productReview.replies.length > displayedReplies && (
                <button className="hover:bg-custom-main-bg text-amber-500 hover:text-black py-1 px-3 text-[.8rem] leading-none rounded-2xl flex justify-center items-center" onClick={handleShowMoreReplies}>
                  Show {Math.min(5, productReview.replies.length - displayedReplies)} more
                </button>
              )}
              {displayedReplies > 3 && (
                <button className="hover:bg-custom-main-bg text-amber-500 hover:text-black py-1 px-3 text-[.8rem] leading-none rounded-2xl flex justify-center items-center" onClick={handleShowLessReplies}>
                  Show less
                </button>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={`${!isSuccess && "mt-4"} w-full flex flex-col justify-start items-end`}>
      {content}
    </div>
  );
};

export default ProductReview;